import { I18n } from '@affine/i18n';

import type { WorkspaceRequestCategory } from './request-coordinator';

export type BackendDegradationScope =
  | 'global-backend'
  | 'workspace-tenant-data'
  | 'metadata'
  | 'permissions'
  | 'ai'
  | 'sync';

export type DegradationSeverity = 'silent' | 'inline' | 'blocking';

export interface ScopedDegradationKey {
  scope: BackendDegradationScope;
  workspaceId?: string;
}

export interface ScopedDegradationState {
  key: ScopedDegradationKey;
  degraded: boolean;
  code?: string;
  message?: string;
  requestId?: string;
  retryable: boolean;
  severity: DegradationSeverity;
  updatedAt: number;
}

export interface DegradationMarkInput {
  key: ScopedDegradationKey;
  sourceId?: string;
  code?: string;
  message?: string;
  requestId?: string;
  retryable?: boolean;
  severity?: DegradationSeverity;
}

export interface ToastPolicyInput {
  mode: 'foreground' | 'background';
  category?: WorkspaceRequestCategory;
}

const RAW_TECHNICAL_ERROR_PATTERN =
  /\bhttp\s*5\d\d\b|\b5\d\d\b|http504|localhost:\d+|127\.0\.0\.1:\d+|tenant[-_ ]?[a-z0-9]+|shard[-_ ]?[a-z0-9]+/i;

const WORKSPACE_ROUTE_CODES = new Set([
  'DATABASE_SHARD_NOT_FOUND',
  'DATABASE_SHARD_ASSIGNMENT_MISSING',
  'DATABASE_SHARD_CONNECTION_NOT_CONFIGURED',
  'DATABASE_SHARD_UNREACHABLE',
  'DATABASE_SHARD_ENVIRONMENT_MISMATCH',
  'TENANT_DATABASE_MIGRATION_IN_PROGRESS',
  'WORKSPACE_TENANT_ROUTE_UNAVAILABLE',
  'BACKEND_DEPENDENCY_UNAVAILABLE',
]);

export class ScopedDegradationStore {
  private readonly states = new Map<string, ScopedDegradationState>();
  private readonly sources = new Map<
    string,
    Map<string, ScopedDegradationState>
  >();
  private readonly listeners = new Set<
    (state: ScopedDegradationState) => void
  >();

  markDegraded(input: DegradationMarkInput): ScopedDegradationState {
    const state: ScopedDegradationState = {
      key: input.key,
      degraded: true,
      code: input.code,
      message: sanitizeUserFacingError(input.message),
      requestId: input.requestId,
      retryable: input.retryable ?? true,
      severity: input.severity ?? 'inline',
      updatedAt: Date.now(),
    };
    const key = degradationKey(input.key);
    const sources = this.sources.get(key) ?? new Map();
    sources.set(input.sourceId ?? 'default', state);
    this.sources.set(key, sources);
    return this.publishAggregate(key, sources);
  }

  markAvailable(
    key: ScopedDegradationKey,
    sourceId?: string
  ): ScopedDegradationState {
    const storageKey = degradationKey(key);
    const sources = this.sources.get(storageKey);
    if (sourceId) {
      sources?.delete(sourceId);
    } else {
      sources?.clear();
    }
    if (sources?.size) return this.publishAggregate(storageKey, sources);

    this.sources.delete(storageKey);
    const state: ScopedDegradationState = {
      key,
      degraded: false,
      retryable: false,
      severity: 'silent',
      updatedAt: Date.now(),
    };
    this.states.set(storageKey, state);
    this.emit(state);
    return state;
  }

  get(key: ScopedDegradationKey): ScopedDegradationState | undefined {
    return this.states.get(degradationKey(key));
  }

  clearWorkspace(workspaceId: string): void {
    for (const [key, state] of this.states) {
      if (state.key.workspaceId === workspaceId) {
        this.states.delete(key);
        this.sources.delete(key);
        this.emit({
          key: state.key,
          degraded: false,
          retryable: false,
          severity: 'silent',
          updatedAt: Date.now(),
        });
      }
    }
  }

  subscribe(listener: (state: ScopedDegradationState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(state: ScopedDegradationState): void {
    for (const listener of this.listeners) {
      listener(state);
    }
  }

  private publishAggregate(
    key: string,
    sources: Map<string, ScopedDegradationState>
  ): ScopedDegradationState {
    const state = [...sources.values()].sort(compareDegradationState)[0];
    if (!state) throw new Error('Cannot publish an empty degradation scope');
    this.states.set(key, state);
    this.emit(state);
    return state;
  }
}

export const defaultScopedDegradationStore = new ScopedDegradationStore();

export function scopeForRequestCategory(
  category: WorkspaceRequestCategory
): BackendDegradationScope {
  switch (category) {
    case 'metadata':
      return 'metadata';
    case 'permissions':
    case 'permission-graph':
    case 'admin-diagnostics':
      return 'permissions';
    case 'ai-bootstrap':
      return 'ai';
    default:
      return 'workspace-tenant-data';
  }
}

export function shouldSuppressGlobalToast(
  error: unknown,
  policy: ToastPolicyInput
): boolean {
  if (policy.mode === 'background') {
    return isRateLimitError(error) || isBackendDegradationError(error);
  }
  return (
    isBackendDegradationError(error) && policy.category !== 'foreground-action'
  );
}

export function isBackendDegradationError(error: unknown): boolean {
  const code = readErrorCode(error);
  return code ? WORKSPACE_ROUTE_CODES.has(code) : false;
}

export function isRateLimitError(error: unknown): boolean {
  const status = readErrorStatus(error);
  const code = readErrorCode(error);
  return (
    status === 429 ||
    code === 'TOO_MANY_REQUEST' ||
    code === 'RATE_LIMIT_EXCEEDED'
  );
}

export function sanitizeUserFacingError(message: unknown): string | undefined {
  if (typeof message !== 'string' || !message.trim()) return undefined;
  if (RAW_TECHNICAL_ERROR_PATTERN.test(message)) {
    return I18n['com.streamlain.error.workspace-unavailable.message']();
  }
  return message;
}

function compareDegradationState(
  left: ScopedDegradationState,
  right: ScopedDegradationState
): number {
  const rank: Record<DegradationSeverity, number> = {
    blocking: 0,
    inline: 1,
    silent: 2,
  };
  return (
    rank[left.severity] - rank[right.severity] ||
    right.updatedAt - left.updatedAt
  );
}

function degradationKey(key: ScopedDegradationKey): string {
  return `${key.scope}:${key.workspaceId ?? 'global'}`;
}

function readErrorStatus(error: unknown): number | undefined {
  const record = error as Record<string, unknown> | undefined;
  const status = record?.status ?? record?.statusCode;
  return typeof status === 'number' ? status : undefined;
}

function readErrorCode(error: unknown): string | undefined {
  const record = error as Record<string, unknown> | undefined;
  const code =
    record?.code ??
    record?.name ??
    (record?.error as Record<string, unknown> | undefined)?.code ??
    (record?.extensions as Record<string, unknown> | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}
